/**
 * SEOAutopilotPage — dedicated tool subpage. User enters their site URL +
 * chosen frequency + which actions they want the autopilot to take. Backend
 * stores an autopilot document; clicking "Scan now" invokes the LLM-backed
 * scanner and surfaces the list of fixes it would apply.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Zap, Plus, Loader2, Trash2, RefreshCw, Bot,
  CheckCircle2, FileText, Image as ImageIcon, Link as LinkIcon,
  Code, HelpCircle,
} from "lucide-react";
import { ToolHeader, SignInGate } from "./CompetitorSpyPage";
import { OrderNowBottomCTA } from "../components/common/ServiceOrderBundle";

const ACTION_META = {
  meta_titles: { icon: FileText, label: "Meta titles" },
  meta_descriptions: { icon: FileText, label: "Meta descriptions" },
  alt_texts: { icon: ImageIcon, label: "Image alt texts" },
  internal_links: { icon: LinkIcon, label: "Internal links" },
  faq_sections: { icon: HelpCircle, label: "FAQ sections" },
  schema_markup: { icon: Code, label: "Schema markup" },
};
const DEFAULT_ACTIONS = ["meta_titles", "meta_descriptions", "alt_texts", "internal_links", "faq_sections"];

export default function SEOAutopilotPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [form, setForm] = useState({ website_url: "", frequency: "weekly", actions: DEFAULT_ACTIONS });

  useEffect(() => { if (user) fetchList(); }, [user]);  // eslint-disable-line
  const fetchList = async () => {
    const r = await axios.get(`${API}/autopilot/status`, { params: { email: user.email } }).catch(() => null);
    const l = r?.data?.autopilots || [];
    setList(l);
    if (l.length && !selected) { setSelected(l[0]); fetchResults(l[0].autopilot_id); }
  };
  const fetchResults = async (id) => {
    setLoading(true);
    const r = await axios.get(`${API}/autopilot/results/${id}`).catch(() => null);
    setResults(r?.data || null);
    setLoading(false);
  };
  const toggleAction = (k) => setForm((p) => ({
    ...p, actions: p.actions.includes(k) ? p.actions.filter((x) => x !== k) : [...p.actions, k],
  }));
  const handleSetup = async (e) => {
    e.preventDefault();
    if (!form.website_url) return;
    setLoading(true);
    const r = await axios.post(`${API}/autopilot/setup`, {
      website_url: form.website_url, email: user.email, frequency: form.frequency, actions: form.actions,
    }).catch(() => null);
    if (r?.data?.autopilot_id) {
      setShowSetup(false);
      setForm({ website_url: "", frequency: "weekly", actions: DEFAULT_ACTIONS });
      await fetchList();
      setSelected(r.data);
      await runScan(r.data.autopilot_id);
    }
    setLoading(false);
  };
  const runScan = async (id) => {
    setScanning(true);
    await axios.post(`${API}/autopilot/scan`, null, { params: { autopilot_id: id } }).catch(() => null);
    await fetchResults(id);
    await fetchList();
    setScanning(false);
  };
  const handleDelete = async (id) => {
    await axios.delete(`${API}/autopilot/${id}`).catch(() => null);
    setList((p) => p.filter((x) => x.autopilot_id !== id));
    if (selected?.autopilot_id === id) { setSelected(null); setResults(null); }
  };

  const finalCta = (
    <OrderNowBottomCTA
      packageId="addon_autopilot"
      priceEur={79}
      interval="mes"
      serviceName="SEO Autopilot"
      headline="Pusti SEO na autopilot"
      subcopy="Agent sám skenuje tvoj web, generuje fix suggestions a optimalizuje pre AI vyhľadávače. Ty len schvaľuješ."
      testid="autopilot-final-cta"
    />
  );

  if (!user) return (
    <SignInGate title={t.ap_title || "SEO Autopilot"} sub={t.ap_sign_in_desc || "Sign in to auto-fix SEO issues across your site."}>
      {finalCta}
    </SignInGate>
  );
  const latest = results?.results?.[0];

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <ToolHeader />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Bot className="w-6 h-6 text-[#a855f7]" />
              <h1 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tight" data-testid="ap-title">{t.ap_title || "SEO Autopilot"}</h1>
              <span className="px-2 py-0.5 bg-[#a855f7]/15 text-[#c084fc] text-xs font-bold">€79/mo</span>
            </div>
            <p className="text-[var(--color-text-muted)]">{t.ap_desc || "Connect your site once — autopilot finds and fixes SEO issues on its own schedule."}</p>
          </div>
          <button onClick={() => setShowSetup(true)} data-testid="ap-add-btn"
            className="flex items-center gap-2 px-6 py-3 bg-[#a855f7] text-white font-bold uppercase text-sm hover:bg-purple-600">
            <Plus className="w-4 h-4" /> {t.ap_add || "New site"}
          </button>
        </div>

        {showSetup && (
          <div className="fixed inset-0 bg-[var(--color-bg)]/80 z-50 flex items-center justify-center p-4" data-testid="ap-setup-modal">
            <form onSubmit={handleSetup} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8 max-w-lg w-full">
              <h2 className="text-xl font-black font-['Cabinet_Grotesk'] mb-6">{t.ap_setup || "Connect a site"}</h2>
              <label className="block text-sm text-[var(--color-text-muted)] mb-1">{t.ap_url || "Website URL *"}</label>
              <input required value={form.website_url} onChange={(e) => setForm((p) => ({ ...p, website_url: e.target.value }))}
                placeholder="https://yoursite.com" data-testid="ap-url"
                className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none mb-4" />
              <label className="block text-sm text-[var(--color-text-muted)] mb-1">{t.ap_frequency || "Scan frequency"}</label>
              <select value={form.frequency} onChange={(e) => setForm((p) => ({ ...p, frequency: e.target.value }))}
                className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none mb-4" data-testid="ap-frequency">
                <option value="daily">{t.ap_daily || "Daily"}</option>
                <option value="weekly">{t.ap_weekly || "Weekly"}</option>
                <option value="monthly">{t.ap_monthly || "Monthly"}</option>
              </select>
              <label className="block text-sm text-[var(--color-text-muted)] mb-2">{t.ap_actions || "Actions the autopilot may take"}</label>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {Object.entries(ACTION_META).map(([k, m]) => {
                  const on = form.actions.includes(k);
                  return (
                    <button type="button" key={k} onClick={() => toggleAction(k)} data-testid={`ap-action-${k}`}
                      className={`flex items-center gap-2 px-3 py-2 border text-sm text-left ${on ? "border-[#a855f7] bg-[#a855f7]/10 text-white" : "border-[var(--color-border)] text-[var(--color-text-muted)]"}`}>
                      <m.icon className="w-4 h-4" />{m.label}
                    </button>
                  );
                })}
              </div>
              <button type="submit" disabled={loading || !form.website_url} data-testid="ap-setup-submit"
                className="w-full py-3 bg-[#a855f7] text-white font-bold uppercase hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {t.ap_start || "Start autopilot + first scan"}
              </button>
            </form>
          </div>
        )}

        {list.length > 0 && (
          <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
            {list.map((a) => (
              <button key={a.autopilot_id} data-testid={`ap-item-${a.autopilot_id}`} onClick={() => { setSelected(a); fetchResults(a.autopilot_id); }}
                className={`px-5 py-3 border whitespace-nowrap font-bold font-['Cabinet_Grotesk'] ${selected?.autopilot_id === a.autopilot_id ? "bg-[var(--color-surface)] border-[#a855f7]" : "bg-[var(--color-bg)] border-[var(--color-border)] text-[var(--color-text-muted)]"}`}>
                {a.website_url.replace(/^https?:\/\//, "")} · <span className="text-[#c084fc]">{a.total_fixes || 0} fixes</span>
              </button>
            ))}
          </div>
        )}

        {list.length === 0 && !showSetup && (
          <div className="text-center py-20">
            <Bot className="w-16 h-16 text-zinc-700 mx-auto mb-6" />
            <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] mb-3">{t.ap_empty || "No autopilots yet"}</h2>
            <p className="text-[var(--color-text-muted)] mb-8 max-w-md mx-auto">{t.ap_empty_sub || "Connect a website to let the autopilot find and fix SEO issues automatically."}</p>
            <button onClick={() => setShowSetup(true)} data-testid="ap-first-btn" className="px-8 py-4 bg-[#a855f7] text-white font-bold uppercase text-lg hover:bg-purple-600">
              <Plus className="w-5 h-5 inline mr-2" />{t.ap_connect || "Connect a site"}
            </button>
          </div>
        )}

        {selected && latest && (
          <div className="space-y-6" data-testid="ap-results">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black font-['Cabinet_Grotesk']">{selected.website_url.replace(/^https?:\/\//, "")}</h2>
              <div className="flex gap-3">
                <button onClick={() => runScan(selected.autopilot_id)} disabled={scanning} data-testid="ap-rescan"
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] text-sm font-bold hover:border-zinc-500 disabled:opacity-50">
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}{t.ap_rescan || "Scan now"}
                </button>
                <button onClick={() => handleDelete(selected.autopilot_id)} className="p-2 text-[var(--color-text-muted)] hover:text-red-500" data-testid="ap-delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4" data-testid="ap-summary">
              <StatCard label={t.ap_fixes_total || "Fixes this scan"} value={latest.fixes?.length || latest.total_fixes || 0} />
              <StatCard label={t.ap_score_before || "Score before"} value={`${latest.score_before ?? "—"}${latest.score_before != null ? "/100" : ""}`} />
              <StatCard label={t.ap_score_after || "Score after"} value={`${latest.score_after ?? "—"}${latest.score_after != null ? "/100" : ""}`} highlight />
            </div>

            {latest.fixes?.length > 0 && (
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6" data-testid="ap-fix-list">
                <h3 className="text-sm font-bold text-[#FFE600] uppercase tracking-wider mb-4 font-['JetBrains_Mono']">{t.ap_fixes_applied || "Fixes applied"}</h3>
                <div className="space-y-3">
                  {latest.fixes.map((f, i) => {
                    const m = ACTION_META[f.type] || { icon: CheckCircle2, label: f.type };
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 bg-[var(--color-bg)] border border-[var(--color-border)]" data-testid={`ap-fix-${i}`}>
                        <m.icon className="w-4 h-4 mt-0.5 text-[#c084fc] flex-shrink-0" />
                        <div className="flex-1 text-sm">
                          <div className="font-bold">{m.label} · {f.page || f.target || ""}</div>
                          <div className="text-[var(--color-text-muted)] text-xs">{f.description || f.summary}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] text-center">{t.ap_last_scan || "Last scan"}: {latest.scanned_at ? new Date(latest.scanned_at).toLocaleString() : "—"}</p>
          </div>
        )}

        {finalCta}
      </div>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`p-6 border ${highlight ? "bg-[#a855f7]/10 border-[#a855f7]/30" : "bg-[var(--color-surface)] border-[var(--color-border)]"}`}>
      <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-1">{label}</p>
      <p className="text-3xl font-black font-['JetBrains_Mono']">{value}</p>
    </div>
  );
}
